'use client';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Fade, Snackbar, SnackbarCloseReason, SnackbarContent, Typography } from '@mui/material';
import { BaseAlert } from '../base-ui';
import { useTheme } from '@mui/material/styles';
import { MAX_SCREEN_WIDTH } from '../constants/layoutConfig';
import { useGlobalDialog } from './global-dialog';
import { useAuthenticationPopUp } from './authentication-popup-context';

interface SnackbarMessage {
    message: string;
    severity?: 'info' | 'success' | 'warning' | 'error';
    key?: number;
    autoHideDuration?: number;
    vertical?: 'top' | 'bottom';
    horizontal?: 'left' | 'right' | 'center';
    type?: 'toast' | 'alert';
}

interface SnackbarContextType {
    addSnackbar: (snackbar: SnackbarMessage) => void;
    marginBottom: number;
    setMarginBottom: React.Dispatch<React.SetStateAction<number>>;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
    children: ReactNode;
}

interface SnackbarItemProps {
    message: SnackbarMessage;
    index: number;
    baseMarginBottom: number;
    onExited: (key: number) => void;
}

const SnackbarItem: React.FC<SnackbarItemProps> = ({ message, index, baseMarginBottom, onExited }) => {
    const [open, setOpen] = useState(true);
    const theme = useTheme();

    const handleClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === 'clickaway') return;
        setOpen(false);
    };

    const handleExited = () => {
        if (message.key) onExited(message.key);
    };

    return (
        <Snackbar
            key={message.key}
            disableWindowBlurListener
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            TransitionComponent={Fade}
            anchorOrigin={{
                vertical: message.vertical ?? 'bottom',
                horizontal: message.horizontal ?? 'center',
            }}
            autoHideDuration={message.autoHideDuration ?? 3000}
            TransitionProps={{ onExited: handleExited }}
            sx={{
                '@media (min-width:600px)': {
                    width: '95%',
                    maxWidth: 'calc(768px - 50px)',
                },
                color: '#fff',
                background: 'rgba(0, 0, 0, 0.60)',
                margin: '0 auto',
                fontSize: theme.typography.h2_12Regular,
                borderRadius: 4,
                padding: '11px 26px 11px 14px',
                width: 'auto',
                right: '20px',
                left: '20px',
                maxWidth: MAX_SCREEN_WIDTH,
                mb: `${baseMarginBottom + index * 60}px`,
                transition: 'margin-bottom 0.3s ease-out',
                backdropFilter: 'blur(5px)',
                zIndex: 10000 + index,
            }}
        >
            <SnackbarContent
                sx={{
                    '.MuiSnackbarContent-message': { py: 0, px: 0 },
                    fontSize: '12px',
                    fontWeight: 400,
                    width: '100%',
                    color: '#fff',
                    background: 'none',
                    boxShadow: 'none',
                    py: 0,
                    px: 0,
                }}
                message={<Typography variant="h2_12Regular" sx={{ whiteSpace: 'pre-line' }}>{message.message}</Typography>}
            />
        </Snackbar>
    );
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [activeSnacks, setActiveSnacks] = useState<SnackbarMessage[]>([]);
    const { isDialogOpen } = useAuthenticationPopUp();
    const [marginBottom, setMarginBottom] = useState(20);

    const addSnackbar = (snackbar: SnackbarMessage) => {
        setActiveSnacks((prev) => [
            ...prev,
            { ...snackbar, key: snackbar.key || new Date().getTime() + Math.random() }
        ]);
    };

    const handleExited = (key: number) => {
        setActiveSnacks((prev) => prev.filter((s) => s.key !== key));
    };

    useEffect(() => {
        if (isDialogOpen) {
            setActiveSnacks([]);
        }
    }, [isDialogOpen]);

    return (
        <SnackbarContext.Provider value={{ addSnackbar, marginBottom, setMarginBottom }}>
            {children}
            {!isDialogOpen && activeSnacks.map((snack, index) => (
                <SnackbarItem
                    key={snack.key}
                    message={snack}
                    index={index}
                    baseMarginBottom={marginBottom}
                    onExited={handleExited}
                />
            ))}
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = (): SnackbarContextType => {
    const context = useContext(SnackbarContext);
    if (context === undefined) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};
